import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { IconButton, Skeleton, Typography } from "@mui/material";
import { sameOriginImageURL } from "@shared/helpers/constants";
import clsx from "clsx";
import { type FC } from "react";
import type { BackgroundImageItem } from "./imageGalleryApi";
import { useLazyBackgroundImages } from "./useLazyBackgroundImages";

export type { BackgroundImageItem };

type Props = {
  images: BackgroundImageItem[] | undefined;
  selectedImageId?: string;
  onSelectImage?: (imageId: string) => void;
  showDeleteIcon?: boolean;
  onDeleteImage?: (imageId: string) => void;
  className?: string;
  showUsage?: boolean;
  compact?: boolean;
};

export const OrganizationBackgroundImagesGallery: FC<Props> = ({
  images,
  selectedImageId,
  onSelectImage,
  showDeleteIcon = false,
  onDeleteImage,
  className,
  showUsage = false,
  compact = false,
}) => {
  const imageIds = images?.map((image) => image.imageId) ?? [];
  const { setFrameNode, startedIds, loadedImagesStatus, markLoaded, markError } =
    useLazyBackgroundImages(imageIds, selectedImageId);

  return (
    <div className={clsx("images-container", compact && "images-compact", className)}>
      {images?.map((image) => {
        const status = loadedImagesStatus[image.imageId];
        const started = startedIds.has(image.imageId);
        return (
          <div
            className="image-container"
            key={image.imageId}
            style={{ position: "relative", display: "block" }}
          >
            <div
              ref={(node) => setFrameNode(image.imageId, node)}
              className={clsx(
                "image",
                selectedImageId === image.imageId && "chosen",
              )}
              onClick={
                onSelectImage ? () => onSelectImage(image.imageId) : undefined
              }
              style={{
                overflow: "hidden",
                position: "relative",
                cursor: onSelectImage ? "pointer" : undefined,
              }}
            >
              {started && (
                <img
                  src={sameOriginImageURL(image.imageId)}
                  alt="תמונת רקע"
                  onLoad={() => markLoaded(image.imageId)}
                  onError={() => markError(image.imageId)}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                    opacity: status === "loaded" ? 1 : 0,
                  }}
                />
              )}
              {status !== "loaded" && (
                <Skeleton
                  variant="rounded"
                  animation={status === "error" ? false : "wave"}
                  sx={{
                    position: "absolute",
                    inset: 0,
                    transform: "none",
                    width: "100%",
                    height: "100%",
                  }}
                />
              )}
              {status === "error" && (
                <Typography
                  variant="caption"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    color: "text.secondary",
                    bgcolor: "grey.100",
                    fontSize: "0.65rem",
                  }}
                >
                  הקובץ לא נטען
                </Typography>
              )}
              {showDeleteIcon && onDeleteImage && (
                <IconButton
                  size="small"
                  aria-label="מחיקה"
                  onClick={(event) => {
                    event.stopPropagation();
                    onDeleteImage(image.imageId);
                  }}
                  sx={{
                    position: "absolute",
                    top: 2,
                    insetInlineEnd: 2,
                    zIndex: 1,
                    p: 0.25,
                    bgcolor: "rgba(255,255,255,0.92)",
                    "&:hover": { bgcolor: "common.white" },
                  }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </div>
            {showUsage && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  width: compact ? "7.25rem" : "8.5rem",
                  mt: 0.25,
                  lineHeight: 1.25,
                  textAlign: "center",
                  fontSize: "0.65rem",
                }}
              >
                {image.isOrganizationDefault && image.opportunityCount === 0
                  ? "ברירת מחדל"
                  : image.opportunityCount > 0
                    ? `ב-${image.opportunityCount} משימות`
                    : "לא בשימוש"}
              </Typography>
            )}
          </div>
        );
      })}
    </div>
  );
};
